export interface TransactionNote {
  id: string
  author: string
  role: string
  timestamp: string
  content: string
  flagged?: boolean
}

export interface Mint {
  id: string
  name: string
  symbol: string
  decimals: number
  address?: string | null
  chain: string
  image?: string | null
}

export interface SwapTransactionData {
  id: string
  companyId: string
  company: string
  group: string
  requestedById?: string
  requestedBy?: string
  decisionById?: string
  decisionBy?: string
  fromMint: Mint
  toMint: Mint
  fromAmount: string
  toAmount: string
  lpFee?: string
  usdValue?: string
  status: SwapStatus
  lpReference?: string
  lpWithdrawalTxHash?: string
  pushoutWithdrawalTxHash?: string
  createdAt: string
  updatedAt: string
  decisionAt?: string
  completedAt?: string
  whitelistedAddress: string
  isFlagged: boolean
  notes?: TransactionNote[]
}

export interface DepositTransactionData {
  id: string
  status: TransactionStatus
  type: string
  currency: string
  symbol: string
  amount: string
  user: string
  company: string
  companyId: string
  group: string
  groupId: string
  decisionBy?: string
  transactionId: string
  creationDate: string
  decisionDate?: string
  finalizedDate?: string
  depositTransactionHash: string
  depositAddress: string
  notes: TransactionNote[]
  isFlagged: boolean
}

export enum SwapStatus {
  Approved = 'APPROVED',
  Cancelled = 'CANCELLED',
  Completed = 'COMPLETED',
  PushOutCompleted = 'PUSH_OUT_COMPLETED',
  PushOutFailed = 'PUSH_OUT_FAILED',
  PushOutInitiated = 'PUSH_OUT_INITIATED',
  Rejected = 'REJECTED',
  Requested = 'REQUESTED',
  TradeCompleted = 'TRADE_COMPLETED',
  TradeExecuting = 'TRADE_EXECUTING',
  TradeFailed = 'TRADE_FAILED',
  WithdrawalCompleted = 'WITHDRAWAL_COMPLETED',
  WithdrawalFailed = 'WITHDRAWAL_FAILED',
  WithdrawalInitiated = 'WITHDRAWAL_INITIATED',
}

export enum DepositStatus {
  Approved = 'APPROVED',
  Completed = 'COMPLETED',
  Failed = 'FAILED',
  Initiated = 'INITIATED',
  LpTransferCompleted = 'LP_TRANSFER_COMPLETED',
  LpTransferFailed = 'LP_TRANSFER_FAILED',
  LpTransferInitiated = 'LP_TRANSFER_INITIATED',
  Rejected = 'REJECTED',
  RejectedCompleted = 'REJECTED_COMPLETED',
  RejectedFailed = 'REJECTED_FAILED',
}

export enum TransactionStatus {
  Approved = 'APPROVED',
  Awaiting = 'AWAITING',
  Completed = 'COMPLETED',
  Pending = 'PENDING',
  Rejected = 'REJECTED',
}

export enum TransactionType {
  Deposit = 'DEPOSIT',
  Trade = 'TRADE',
}

export interface TransactionBreadcrumb {
  label: string
  onClick?: () => void
  imageUrl?: string
}

export interface TransactionHeaderProps {
  transactionId: string
  company?: string
  companyImageUrl?: string
  companyTradeType?: string
  status: SwapStatus | DepositStatus
  transactionType: 'TRADE' | 'DEPOSIT'
  showActions?: boolean
  onApprove?: () => void
  onReject?: () => void
  isApproving?: boolean
  isRejecting?: boolean
  isComplianceUser?: boolean
  isFlagged?: boolean
  isExternalApi?: boolean | null
}

export interface TransactionNotesProps {
  notes: TransactionNote[]
  newNote: string
  onNewNoteChange: (note: string) => void
  onSendNote: () => void
  isSendingNote: boolean
  isFlagged: boolean
  onFlagToggle: (flagged: boolean) => void
  isFlagging: boolean
  showFlagToggle?: boolean
}

export interface CopyableField {
  label: string
  value: string
  fieldKey: string
}

export interface TransactionDetailsProps {
  fields: CopyableField[]
  onCopy: (text: string, field: string) => void
}

export interface CurrencyAmountProps {
  amount: string
  currency: string
  symbol: string
  chain: string
  image?: string
  label: string
  company?: string
  companyImage?: string
}

export const StepState = {
  Pending: 'PENDING',
  Loading: 'LOADING',
  Completed: 'COMPLETED',
  Failed: 'FAILED',
} as const

export type StepState = (typeof StepState)[keyof typeof StepState]

export interface TransactionStatusProgressProps {
  status: SwapStatus
  steps: string[]
  getStepState: (stepIndex: number) => StepState
  getProgressValue: (status: SwapStatus) => number
}
